/**
 * 需求来源：docs/ODM页面修改/AI提示词.md、docs/ODM页面修改/ODM.html
 * 注意事项：
 * 1. /odm 为纯展示型 SSR 页面，保留既有登录校验和移动端弹窗限制。
 * 2. 页面结构与文案按目标稿重构，不改动后端接口与跳转协议。
 */
import React, { useState, useCallback, useContext, createContext, useMemo } from 'react';
import withSSR from '../../hoc/withSSR';
import fetchData from './fetch';
import style from './index.less';
import {
  getConstants,
  LINKS,
} from './constants';

const CdnHostContext = createContext('');
const ConstantsContext = createContext({});

function joinClassNames() {
  return Array.prototype.slice.call(arguments).filter(Boolean).join(' ');
}

function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  var userAgent = (window.navigator && window.navigator.userAgent) || '';
  var isMobileUa = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(userAgent);
  var hasCoarsePointer = window.matchMedia ? window.matchMedia('(pointer: coarse)').matches : false;
  var isNarrowViewport = window.matchMedia ? window.matchMedia('(max-width: 900px)').matches : window.innerWidth <= 900;
  return isMobileUa || (hasCoarsePointer && isNarrowViewport);
}

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * 需求来源：ODM 页面调整。
 * 注意事项：四个 CTA 统一校验登录后跳转到 requestForm，移动端继续保留弹窗限制。
 */
function handleCtaClick(url, setShowModal) {
  if (typeof window === 'undefined') return;
  if (isMobileDevice()) {
    setShowModal(true);
    return;
  }
  var token = getCookie('cjLoginToken');
  if (!token) {
    var targetUrl = window.location.origin + url;
    window.location.href = '/login.html?target=' + btoa(targetUrl);
    return;
  }
  window.location.href = url;
}

function Icon({ name, className }) {
  var icons = {
    verified: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="18" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M16 24L22 30L34 18" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /></svg>
    ),
    packaging: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 17L24 9L38 17V33L24 41L10 33V17Z" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M10 17L24 25L38 17M24 25V41" fill="none" stroke="currentColor" strokeWidth="4" /></svg>
    ),
    cost: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><rect x="12" y="8" width="24" height="32" rx="4" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M18 18H30M18 26H30M18 34H25" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
    ),
    support: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="18" r="8" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M10 40C12.8 31.5 18 28 24 28C30 28 35.2 31.5 38 40" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
    ),
    basic: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 18L24 10L38 18V34L24 42L10 34V18Z" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M10 18L24 26L38 18M24 26V42" fill="none" stroke="currentColor" strokeWidth="4" /></svg>
    ),
    premium: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 6L30 18H43L33 27L37 41L24 33L11 41L15 27L5 18H18L24 6Z" fill="none" stroke="currentColor" strokeWidth="4" strokeLinejoin="round" /></svg>
    ),
    team: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 11a4 4 0 1 0 0-8a4 4 0 0 0 0 8Z" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M2.8 21c.8-4.2 3.1-6.3 6.2-6.3c3.1 0 5.4 2.1 6.2 6.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M17 8.5a3 3 0 1 0 0-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M16.5 14.4c2.4.4 4 2.5 4.7 6.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    ),
    products: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5h10.5a3 3 0 0 1 3 3v4a3 3 0 0 1-3 3H10l-5 4v-4H4a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3h1Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M8 9h7M8 12h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    ),
    response: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.8v5M12 16.2v5M4.8 4.8l3.5 3.5M15.7 15.7l3.5 3.5M2.8 12h5M16.2 12h5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg>
    ),
    suppliers: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.8 14.2 14.2 9.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M10.7 5.2 12.2 3.7a4.3 4.3 0 0 1 6.1 6.1l-2.1 2.1a4.3 4.3 0 0 1-5.5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M13.3 18.8 11.8 20.3a4.3 4.3 0 0 1-6.1-6.1l2.1-2.1a4.3 4.3 0 0 1 5.5-.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    ),
    analysis: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5 20 6v6.2c0 5-3.4 8.1-8 9.3c-4.6-1.2-8-4.3-8-9.3V6l8-3.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M12 7.5v9M15 9.5c-.5-1-1.5-1.5-3-1.5c-1.7 0-2.8.8-2.8 2c0 1.4 1.5 1.8 3.1 2.1c1.7.4 3.1.8 3.1 2.2c0 1.2-1.1 2-2.9 2c-1.7 0-2.8-.6-3.4-1.7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
    ),
    sourcing: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21a9 9 0 1 0 0-18a9 9 0 0 0 0 18Z" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M8 13a2 2 0 1 0 0-4a2 2 0 0 0 0 4ZM14.8 9.2a1.7 1.7 0 1 0 0-3.4a1.7 1.7 0 0 0 0 3.4ZM15.2 17.2a2 2 0 1 0 0-4a2 2 0 0 0 0 4Z" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>
    ),
    customization: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4.5 19.5 9 9 19.5H4.5V15L15 4.5Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M13.5 6 18 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    ),
    branding: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 4h4l1.2 3.1 3.3.5 1.2 3.7-2.4 2.4.6 3.3-3.1 2.2L12 17.7l-2.8 1.5-3.1-2.2.6-3.3-2.4-2.4 1.2-3.7 3.3-.5L10 4Z" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinejoin="round" /><circle cx="12" cy="12" r="2.8" fill="none" stroke="currentColor" strokeWidth="1.65" /></svg>
    ),
    best: (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l2.7 5.4 6 .9-4.3 4.2 1 6-5.4-2.8-5.4 2.8 1-6-4.3-4.2 6-.9L12 3Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
    ),
    stepIdea: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M12 10H36V38H12V10Z" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M18 18H30M18 26H30M18 34H25" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
    ),
    stepAnalysis: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M8 36C16 20 24 32 32 16C36 8 42 10 42 10" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /><path d="M34 10H42V18" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
    ),
    stepCustomize: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M15 34L14 40L20 39L36 23L31 18L15 34Z" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M28 21L33 26" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>
    ),
    stepProduce: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 17L24 9L38 17V33L24 41L10 33V17Z" fill="none" stroke="currentColor" strokeWidth="4" /><path d="M10 17L24 25L38 17M24 25V41" fill="none" stroke="currentColor" strokeWidth="4" /></svg>
    ),
    stepLaunch: (
      <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M24 6L30 18H43L33 27L37 41L24 33L11 41L15 27L5 18H18L24 6Z" fill="none" stroke="currentColor" strokeWidth="4" strokeLinejoin="round" /></svg>
    ),
  };

  return <span className={className}>{icons[name] || null}</span>;
}

function SectionHeading({ eyebrow, title, subtitle, wide }) {
  return (
    <div className={joinClassNames(style.sectionHead, wide ? style.sectionHeadWide : '')}>
      {eyebrow ? <span className={style.sectionLabel}>{eyebrow}</span> : null}
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
  );
}

function HeroSection({ onCtaClick }) {
  const { HERO } = useContext(ConstantsContext);
  return (
    <section className={style.heroSection}>
      <div className={joinClassNames(style.bgIcon, style.bgIcon1)}>
        <svg viewBox="0 0 220 180" aria-hidden="true"><rect x="20" y="30" width="150" height="100" rx="14" fill="none" stroke="currentColor" strokeWidth="3" /><path d="M20 62H170M58 30V130M85 86H140M85 106H125" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /><circle cx="45" cy="46" r="5" fill="currentColor" /><circle cx="70" cy="46" r="5" fill="currentColor" /></svg>
      </div>
      <div className={joinClassNames(style.bgIcon, style.bgIcon2)}>
        <svg viewBox="0 0 220 180" aria-hidden="true"><path d="M55 60L110 35L165 60V125L110 152L55 125V60Z" fill="none" stroke="currentColor" strokeWidth="3" /><path d="M55 60L110 88L165 60M110 88V152M88 48L143 76" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" /></svg>
      </div>
      <div className={style.container}>
        <div className={style.heroGrid}>
          <div className={style.heroContent}>
            <span className={style.badge}><span className={style.badgeDot} />{HERO.label}</span>
            <h1>{HERO.title} <span>{HERO.titleHighlight}</span></h1>
            <p>{HERO.subtitle}</p>
            <div className={style.heroFeatureGrid}>
              {HERO.features.map(function(feature) {
                return (
                  <div key={feature.title} className={style.heroFeatureCard}>
                    <div className={style.heroFeatureIcon}>
                      <Icon name={feature.icon} />
                    </div>
                    <div>
                      <h3>{feature.title}</h3>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              className={style.primaryBtn}
              onClick={function() {
                onCtaClick(LINKS.freeOdm);
              }}
            >
              {HERO.cta}
              <span>→</span>
            </button>
          </div>

          <div className={style.heroMedia}>
            <div className={style.heroImageWrap}>
              <div className={joinClassNames(style.orbit, style.orbit1)}><Icon name="packaging" /></div>
              <div className={joinClassNames(style.orbit, style.orbit2)}><Icon name="stepCustomize" /></div>
              <div className={joinClassNames(style.floatingCard, style.floatingCard1)}>
                <strong>{HERO.floatingCards[0].title}</strong>
                <span>{HERO.floatingCards[0].text}</span>
              </div>
              <div className={joinClassNames(style.floatingCard, style.floatingCard2)}>
                <strong>{HERO.floatingCards[1].title}</strong>
                <span>{HERO.floatingCards[1].text}</span>
              </div>
              <img src={HERO.image} alt={HERO.imageAlt} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DecisionGuide({ onCtaClick }) {
  const { DECISION } = useContext(ConstantsContext);
  return (
    <section className={joinClassNames(style.softSection, style.overlapSection)}>
      <div className={style.container}>
        <SectionHeading eyebrow={DECISION.eyebrow} title={DECISION.title} subtitle={DECISION.subtitle} />
        <div className={style.choiceGrid}>
          <div className={style.choiceCard}>
            <div className={style.choiceIcon}><Icon name="basic" /></div>
            <div>
              <span className={style.choiceKicker}>{DECISION.basic.kicker}</span>
              <h3>{DECISION.basic.title}</h3>
              <p>{DECISION.basic.description}</p>
              <ul>
                {DECISION.basic.items.map(function(item) {
                  return <li key={item}>{item}</li>;
                })}
              </ul>
            </div>
          </div>
          <div className={style.choiceOr}>{DECISION.orLabel}</div>
          <div className={joinClassNames(style.choiceCard, style.choiceCardPremium)}>
            <div className={style.choiceRibbon}>{DECISION.premium.ribbon}</div>
            <div className={style.choiceIcon}><Icon name="premium" /></div>
            <div>
              <span className={style.choiceKicker}>{DECISION.premium.kicker}</span>
              <h3>{DECISION.premium.title}</h3>
              <p>{DECISION.premium.description}</p>
              <ul>
                {DECISION.premium.items.map(function(item) {
                  return <li key={item}>{item}</li>;
                })}
              </ul>
            </div>
          </div>
        </div>
        <div className={style.decisionCtaWrap}>
          <button
            className={style.primaryBtn}
            onClick={function() {
              onCtaClick(LINKS.freeOdm);
            }}
          >
            {DECISION.cta}
            <span>→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const { COMPARISON } = useContext(ConstantsContext);
  return (
    <section className={style.softSection}>
      <div className={style.container}>
        <SectionHeading eyebrow={COMPARISON.eyebrow} title={COMPARISON.title} subtitle={COMPARISON.subtitle} />
        <div className={style.comparisonLayout}>
          <div className={style.comparisonDesktop} aria-label="ODM service comparison">
            <div className={joinClassNames(style.comparisonCard, style.comparisonServiceCard)}>
              <div className={style.comparisonHead}>{COMPARISON.serviceHead}</div>
              {COMPARISON.rows.map(function(row) {
                return (
                  <div key={row.label} className={joinClassNames(style.comparisonRow, row.final ? style.comparisonRowFinal : '')}>
                    <span className={style.comparisonRowIcon}><Icon name={row.icon} /></span>
                    <p>{row.label}</p>
                  </div>
                );
              })}
            </div>
            <div className={joinClassNames(style.comparisonCard, style.comparisonBasicCard)}>
              <div className={style.comparisonHead}>{COMPARISON.basic.title}</div>
              {COMPARISON.rows.map(function(row) {
                return (
                  <div key={row.label} className={joinClassNames(style.comparisonRow, row.final ? style.comparisonRowFinal : '')}>
                    <span className={style.comparisonDot}>✓</span>
                    <p>{row.basic}</p>
                  </div>
                );
              })}
            </div>
            <div className={joinClassNames(style.comparisonCard, style.comparisonPremiumCard)}>
              <div className={style.comparisonHead}>
                <span className={style.comparisonCrown}><Icon name="premium" /></span>
                {COMPARISON.premium.title}
              </div>
              {COMPARISON.rows.map(function(row) {
                return (
                  <div key={row.label} className={joinClassNames(style.comparisonRow, row.final ? style.comparisonRowFinal : '')}>
                    <span className={joinClassNames(style.comparisonDot, style.comparisonDotPremium)}>✓</span>
                    <p>
                      {row.premiumStrong ? <strong>{row.premiumStrong}</strong> : null}
                      {row.premiumStrong && row.premiumRest ? ' ' : null}
                      {row.premiumRest ? <span>{row.premiumRest}</span> : null}
                      {row.premiumEmphasis ? <em>{row.premiumEmphasis}</em> : null}
                      {!row.premiumStrong && !row.premiumRest && !row.premiumEmphasis ? row.premium : null}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={style.comparisonMobile} aria-label="ODM service comparison mobile">
            {COMPARISON.rows.map(function(row) {
              return (
                <div key={row.label} className={style.mobileCompareItem}>
                  <h3>{row.label}</h3>
                  <div>
                    <span>{COMPARISON.basic.title}</span>
                    <p>{row.basic}</p>
                  </div>
                  <div className={style.premium}>
                    <span>{COMPARISON.premium.title}</span>
                    <p>{row.premiumMobile || row.premium || [row.premiumStrong, row.premiumRest].join(' ')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <p className={style.comparisonNote}>{COMPARISON.note}</p>
      </div>
    </section>
  );
}

function ProductIdeasSection() {
  const { PRODUCTS } = useContext(ConstantsContext);
  return (
    <section className={style.productsSection}>
      <div className={style.container}>
        <SectionHeading eyebrow={PRODUCTS.eyebrow} title={PRODUCTS.title} subtitle={PRODUCTS.subtitle} />
        <div className={style.productsGrid}>
          {PRODUCTS.items.map(function(item) {
            return (
              <article key={item.title} className={joinClassNames(style.productCard, item.featured ? style.productCardFeatured : '')}>
                <div className={style.productImage}>
                  <img src={item.image} alt={item.alt} loading="lazy" />
                </div>
                <div className={style.productContent}>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ValueSection() {
  const { VALUE } = useContext(ConstantsContext);
  return (
    <section className={style.softSection}>
      <div className={style.container}>
        <SectionHeading eyebrow={VALUE.eyebrow} title={VALUE.title} subtitle={VALUE.subtitle} wide />
        <div className={style.valueLayout}>
          <div className={style.valueVisual}>
            <img src={VALUE.visual.image} alt={VALUE.visual.alt} loading="lazy" />
            <div className={joinClassNames(style.valueFloat, style.valueFloatTop)}>{VALUE.visual.topTag}</div>
            <div className={joinClassNames(style.valueFloat, style.valueFloatBottom)}>{VALUE.visual.bottomTag}</div>
          </div>
          <div className={style.valueCards}>
            {VALUE.cards.map(function(card) {
              return (
                <article key={card.title} className={style.valueCard}>
                  <img src={card.image} alt={card.alt} loading="lazy" />
                  <div>
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                    <ul>
                      {card.items.map(function(item) {
                        return <li key={item}>{item}</li>;
                      })}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function WorkflowSection() {
  const { WORKFLOW } = useContext(ConstantsContext);
  return (
    <section className={joinClassNames(style.softSection, style.overlapSection)}>
      <div className={style.container}>
        <SectionHeading eyebrow={WORKFLOW.eyebrow} title={WORKFLOW.title} subtitle={WORKFLOW.subtitle} />
        <div className={style.workflow}>
          {WORKFLOW.items.map(function(item) {
            return (
              <div key={item.step} className={joinClassNames(style.workflowStep, item.featured ? style.workflowStepFeatured : '')}>
                <span>{item.step}</span>
                <div className={style.workflowIcon}><Icon name={item.icon} /></div>
                <h3>{item.title}</h3>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FaqSection({ onCtaClick }) {
  const { FAQ } = useContext(ConstantsContext);
  const [openItems, setOpenItems] = useState([0]);

  const toggleItem = useCallback(function(index) {
    setOpenItems(function(prev) {
      if (prev.indexOf(index) > -1) {
        return prev.filter(function(item) {
          return item !== index;
        });
      }
      return [index];
    });
  }, []);

  return (
    <section className={style.softSection}>
      <div className={style.container}>
        <SectionHeading eyebrow={FAQ.eyebrow} title={FAQ.title} subtitle={FAQ.subtitle} />
        <div className={style.faqLayout}>
          <div className={style.faqPanel}>
            <div className={style.faqPanelIcon}>?</div>
            <h3>{FAQ.panelTitle}</h3>
            <p>{FAQ.panelDescription}</p>
            <button
              className={joinClassNames(style.primaryBtn, style.primaryBtnWide)}
              onClick={function() {
                onCtaClick(LINKS.freeOdm);
              }}
            >
              {FAQ.panelCta}
              <span>→</span>
            </button>
          </div>
          <div className={style.faqList}>
            {FAQ.items.map(function(item, index) {
              var isOpen = openItems.indexOf(index) > -1;
              return (
                <div key={item.question} className={joinClassNames(style.faqItem, isOpen ? style.faqItemOpen : '')}>
                  <button type="button" className={style.faqQuestion} onClick={function() { toggleItem(index); }}>
                    {item.question}
                    <span className={style.faqQuestionIcon} />
                  </button>
                  <div className={style.faqAnswer}>
                    <p>{item.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onCtaClick }) {
  const { FINAL_CTA } = useContext(ConstantsContext);
  return (
    <section className={style.finalCta}>
      <div className={style.finalBg} />
      <div className={style.finalContainer}>
        <div className={style.finalContent}>
          <div className={style.finalTag}>{FINAL_CTA.tag}</div>
          <h2>{FINAL_CTA.title} <span>{FINAL_CTA.titleHighlight}</span></h2>
          <button
            className={style.finalBtn}
            onClick={function() {
              onCtaClick(LINKS.freeOdm);
            }}
          >
            {FINAL_CTA.cta}
            <span>→</span>
          </button>
        </div>
        <div className={style.finalVisual}>
          <div className={style.visualTable} />
          <div className={joinClassNames(style.brandCylinder, style.brandCylinderSmall)} />
          <div className={joinClassNames(style.brandCylinder, style.brandCylinderLarge)}><span>{FINAL_CTA.visual.brandText}</span></div>
          <div className={style.brandBox}>
            <span>{FINAL_CTA.visual.brandText}</span>
            <em>{FINAL_CTA.visual.boxText}</em>
          </div>
          <div className={style.designPaper}>
            <div className={joinClassNames(style.paperLine, style.paperLine1)} />
            <div className={joinClassNames(style.paperLine, style.paperLine2)} />
            <div className={joinClassNames(style.paperLine, style.paperLine3)} />
            <div className={style.productSketch} />
          </div>
          <div className={style.designPen} />
          <div className={style.dotWave} />
        </div>
      </div>
    </section>
  );
}

function MobileModal({ visible, onClose }) {
  const { MOBILE_PROMPT } = useContext(ConstantsContext);
  if (!visible) return null;
  return (
    <div className={style.modalOverlay} onClick={onClose}>
      <div className={style.modalContent} onClick={function(e) { e.stopPropagation(); }}>
        <h3 className={style.modalTitle}>{MOBILE_PROMPT.title}</h3>
        <p className={style.modalMessage}>{MOBILE_PROMPT.message}</p>
        <button className={style.modalBtn} onClick={onClose}>{MOBILE_PROMPT.buttonText}</button>
      </div>
    </div>
  );
}

/**
 * 需求来源：ODM 页面目标稿改版。
 * 注意事项：仅消费 fetchData 返回的展示数据，避免把展示逻辑扩散到 SSR 框架层。
 */
function PayOdmPage(props) {
  const pageData = props.pageData || {};
  const cdnHost = pageData.cdnHost || (typeof CDN_HOST !== 'undefined' ? CDN_HOST : '');
  const constants = useMemo(function() {
    return getConstants();
  }, []);
  const [showModal, setShowModal] = useState(false);

  const onCtaClick = useCallback(function(url) {
    handleCtaClick(url, setShowModal);
  }, []);

  const closeModal = useCallback(function() {
    setShowModal(false);
  }, []);

  return (
    <CdnHostContext.Provider value={cdnHost}>
      <ConstantsContext.Provider value={constants}>
        <div className={style.odmPage}>
          <HeroSection onCtaClick={onCtaClick} />
          <DecisionGuide onCtaClick={onCtaClick} />
          <ComparisonSection />
          <ProductIdeasSection />
          <ValueSection />
          <WorkflowSection />
          <FaqSection onCtaClick={onCtaClick} />
          <FinalCta onCtaClick={onCtaClick} />
          <MobileModal visible={showModal} onClose={closeModal} />
        </div>
      </ConstantsContext.Provider>
    </CdnHostContext.Provider>
  );
}

PayOdmPage.fetch = fetchData;

export default withSSR(PayOdmPage);
